import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  AiChatMessageRecordSchema,
  ChatDetailsQuerySchema,
  ChatItemSchema,
  ChatMessageRecordSchema,
  DataframeSchema,
  GraphTypeSchema,
  UserChatMessageRecordSchema,
  UserMetadataSchema,
  type GraphSchema,
} from '../shared/schemas/ChatSchema.js';
import {
  DocumentUploadRequestSchema,
  RagDocumentImportSourceSchema,
  RagDocumentSchema,
  type RagDocumentStatusSchema,
} from '../shared/schemas/RagDocumentSchema.js';

export type RagDocumentType = z.infer<typeof RagDocumentSchema>;
export type RagDocumentSchemaType = z.infer<typeof RagDocumentStatusSchema>;
export type RagDocumentImportSourceType = z.infer<
  typeof RagDocumentImportSourceSchema
>;
export type DocumentUploadRequestType = z.infer<
  typeof DocumentUploadRequestSchema
>;
export type ChatMessageRecordType = z.infer<typeof ChatMessageRecordSchema>;
export type AiChatMessageRecordType = z.infer<typeof AiChatMessageRecordSchema>;
export type Graph = z.infer<typeof GraphSchema>;
export type GraphType = z.infer<typeof GraphTypeSchema>;
export type DataframeType = z.infer<typeof DataframeSchema>;
export type UserChatMessageRecordType = z.infer<
  typeof UserChatMessageRecordSchema
>;
export type UserMetadataSchemaType = z.infer<typeof UserMetadataSchema>;
export type ChatItemType = z.infer<typeof ChatItemSchema>;
export type ChatDetailsQueryType = z.infer<typeof ChatDetailsQuerySchema>;

export const listRagDocuments = defaultClient.query({
  url: 'ai-assistant/files' as const,
  method: 'GET',
  responseSchema: z.array(RagDocumentSchema),
});

// Same endpoint, scoped to a chat session. Kept as a separate query (rather
// than adding a querySchema to listRagDocuments) so existing callers of
// `listRagDocuments({})` keep their signature unchanged.
export const listRagDocumentsByChat = defaultClient.query({
  url: 'ai-assistant/files' as const,
  method: 'GET',
  responseSchema: z.array(RagDocumentSchema),
  querySchema: z.object({ chatSessionId: z.string() }),
  snakeCaseQueryParams: true,
});

export const loadNewRagDocument = defaultApi.declareEndpoint({
  url: 'ai-assistant/files' as const,
  method: 'POST',
  requestSchema: DocumentUploadRequestSchema,
  responseSchema: RagDocumentSchema,
});

export const setRagDocumentImportSource = defaultClient.mutation({
  url: 'ai-assistant/files/$fileId/import-source' as const,
  method: 'PATCH',
  clientOptions: { headers: { 'Content-Type': 'multipart/form-data' } },
  requestSchema: z.object({ import_source: RagDocumentImportSourceSchema }),
  responseSchema: RagDocumentSchema,
  onSuccess: (queryClient) => {
    listRagDocuments.invalidateAll(queryClient, {});
    listRagDocumentsByChat.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const getRagDocument = defaultApi.declareStream({
  url: 'ai-assistant/files/$fileId' as const,
  method: 'GET',
});

export const deleteRagDocument = defaultApi.declareEndpoint({
  url: 'ai-assistant/files/$documentId' as const,
  method: 'DELETE',
  responseSchema: z.string(),
});

export const getTable = defaultApi.declareStream({
  url: 'ai-assistant/chats/$chatSessionId/messages/$messageId/export' as const,
  method: 'GET',
});

const ReportQuerySchema = z.object({
  s3Path: z.string(),
});

export const getReport = defaultApi.declareStream({
  url: 'ai-assistant/chats/$chatId/file' as const,
  method: 'GET',
  querySchema: ReportQuerySchema,
  snakeCaseQueryParams: true,
});

//TODO: Remove this API call during the refactoring. It's also used in the MINT-IQ package (Argus).
export const getChatList = defaultClient.query({
  url: 'ai-assistant/chats' as const,
  method: 'GET',
  responseSchema: z.array(ChatItemSchema),
  querySchema: ChatDetailsQuerySchema.partial(),
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data;
  },
});

export const getInfiniteChatList = defaultClient.infiniteQuery({
  url: 'ai-assistant/chats' as const,
  method: 'GET',
  responseSchema: z.array(ChatItemSchema),
  querySchema: ChatDetailsQuerySchema.partial(),
  getNextPageParam: (lastPage, _allPages, lastPageParam) => {
    const limit = lastPageParam?.limit ?? 20;
    if (lastPage.length < limit) {
      return undefined;
    }
    return {
      ...lastPageParam,
      offset: (lastPageParam?.offset ?? 0) + limit,
    };
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const createChat = defaultClient.mutation({
  url: 'ai-assistant/chats' as const,
  method: 'POST',
  responseSchema: ChatItemSchema,
  requestSchema: ChatDetailsQuerySchema.partial(),
  onSuccess: (queryClient) => {
    getChatList.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

// mint-iq-specific create: the shared `createChat` request schema (above) has no
// `chatTitle`. Additive — does NOT change `createChat`; reuses the shared
// `ChatItemSchema` response. JSON body keys are snake_cased on the wire
// (scopeType -> scope_type, chatTitle -> chat_title).
export const createChatWithTitle = defaultClient.mutation({
  url: 'ai-assistant/chats' as const,
  method: 'POST',
  responseSchema: ChatItemSchema,
  requestSchema: z.object({
    scopeType: z.enum(['PAGE', 'ENTITY']).optional(),
    scopePath: z.string().optional(),
    chatTitle: z.string().nullish(),
  }),
  onSuccess: (queryClient) => {
    getChatList.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const updateChatTitle = defaultClient.mutation({
  url: 'ai-assistant/chats/$chatSessionId' as const,
  method: 'PATCH',
  responseSchema: ChatItemSchema,
  requestSchema: z.object({
    chatTitle: z.string(),
    chatSessionId: z.string(),
  }),
  onSuccess: (queryClient) => {
    getChatList.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const deleteChat = defaultClient.mutation({
  url: 'ai-assistant/chats/$chatSessionId' as const,
  method: 'DELETE',
  responseSchema: z.string(),
  onSuccess: (queryClient) => {
    getChatList.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});

export const getHistoryById = defaultClient.query({
  url: 'ai-assistant/chats/$chatSessionId' as const,
  method: 'GET',
  responseSchema: z.array(ChatMessageRecordSchema),
  processResponse(data) {
    return data;
  },
});

export const cancelLastRequest = defaultClient.mutation({
  url: 'ai-assistant/chats/$chatSessionId/cancel' as const,
  method: 'POST',
  requestSchema: z.object({
    reason: z.string().nullish(),
  }),
  responseSchema: ChatMessageRecordSchema,
  onSuccess: (queryClient) => {
    getChatList.invalidateAll(queryClient, {});
  },
  processResponse(data) {
    return data;
  },
});
